import { useState } from "react"
import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { BetterDocAnamnesisInfoSchemaTable } from "@/modules/admin/better-doc/components/better-doc-anamnesis-info-schema-table"
import { BetterDocRemoteRequestStatus } from "@/modules/admin/better-doc/components/better-doc-remote-request-status"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import { fetchBetterDocAnamnesisInfoSchemas } from "@/modules/admin/better-doc/services/betterDocAnamnesisInfoSchemasQuery"

type AnamnesisInfoSchemasRequest = {
  attempt: number
}

export const BetterDocAnamnesisInfoSchemas = () => {
  const { t } = useTranslation()
  const [schemasRequest, setSchemasRequest] = useState<AnamnesisInfoSchemasRequest>({ attempt: 0 })
  const schemasState = useBetterDocFetch(schemasRequest, fetchBetterDocAnamnesisInfoSchemas)

  const handleLoad = () => {
    setSchemasRequest((currentRequest) => ({ attempt: currentRequest.attempt + 1 }))
  }

  const renderSchemasState = () => {
    switch (schemasState.status) {
      case "idle":
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-anamnesis-info-schemas-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded":
        return (
          <div className="flex flex-col gap-6">
            <BetterDocRemoteRequestStatus
              status={schemasState.data.status}
              httpStatusCode={schemasState.data.httpStatusCode}
              errorReason={schemasState.data.errorReason}
              exchangeId={schemasState.data.exchangeId}
            />
            {schemasState.data.status === "RECEIVED" && schemasState.data.schemas.length === 0 ? (
              <p
                className="text-on-surface-subdued text-sm"
                data-testid="better-doc-admin-anamnesis-info-schemas-empty">
                {t("admin.betterDoc.anamnesisInfoSchemas.empty")}
              </p>
            ) : null}
            {schemasState.data.schemas.map((schema) => (
              <BetterDocAnamnesisInfoSchemaTable key={`${schema.name}-${schema.version}`} schema={schema} />
            ))}
          </div>
        )
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="better-doc-admin-anamnesis-info-schemas">
      <div className="flex flex-wrap items-center gap-4">
        <Button
          type="button"
          variant="secondary"
          disabled={schemasState.status === "loading"}
          onClick={handleLoad}
          data-testid="better-doc-admin-anamnesis-info-schemas-load-button">
          {t("admin.betterDoc.anamnesisInfoSchemas.load")}
        </Button>
      </div>
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-anamnesis-info-schemas-hint">
        {t("admin.betterDoc.anamnesisInfoSchemas.hint")}
      </p>
      {renderSchemasState()}
    </div>
  )
}
